import reactUpdate from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {
  buildReactionFromComponents,
  BuildReactionFromComponentsResponse,
  ReactionComponentInput,
} from 'chemistry/ChemistryDAO';
import {Status} from 'constants/StatusConstants';
import makeCancelable from 'lib/makeCancelable';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getMoleculeVersionIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  SyncedReactionComponent,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

// Backward compatibility: older persisted tables store syncedComponents as plain molecule ID
// strings. Normalize to tagged union format so isEqual comparisons work correctly.
function normalizeSyncedComponents(
  components: SyncedReactionComponents | undefined
): SyncedReactionComponents | undefined {
  if (components == null) {
    return undefined;
  }
  return {
    [ChemistryReactionRole.REACTANT]: components[ChemistryReactionRole.REACTANT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
    [ChemistryReactionRole.PRODUCT]: components[ChemistryReactionRole.PRODUCT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
  };
}

/**
 * Drop `versionId` from a table-side molecule component when the corresponding diagram-side
 * component hasn't recorded a version yet (migration case -- older tables/diagrams predate
 * version tracking). This function itself is pure. The caller (below) persists the newly-known
 * version immediately, as a one-time backfill with no diagram rebuild, so that a *future*
 * re-versioning of the same molecule is detected as a genuine mismatch instead of being silently
 * dropped again forever. Only a genuine mismatch (both sides recorded, and different) should
 * trigger a rebuild.
 */
function dropUnrecordedVersionMismatches(
  tableComponents: SyncedReactionComponents,
  diagramComponents: SyncedReactionComponents
): SyncedReactionComponents {
  const dropForRole = (role: keyof SyncedReactionComponents): Array<SyncedReactionComponent | string> => {
    const diagramList = diagramComponents[role];
    return tableComponents[role].map((component, index) => {
      if (typeof component === 'string' || component.type !== 'molecule' || component.versionId == null) {
        return component;
      }
      const diagramComponent = diagramList[index];
      const diagramVersionId =
        typeof diagramComponent === 'object' && diagramComponent.type === 'molecule'
          ? diagramComponent.versionId
          : undefined;
      if (diagramVersionId == null) {
        const {versionId, ...withoutVersion} = component;
        return withoutVersion;
      }
      return component;
    });
  };
  return {
    [ChemistryReactionRole.REACTANT]: dropForRole(ChemistryReactionRole.REACTANT),
    [ChemistryReactionRole.PRODUCT]: dropForRole(ChemistryReactionRole.PRODUCT),
  };
}

function tableComponentsMatchDiagram(
  tableComponents: SyncedReactionComponents,
  diagramComponents: SyncedReactionComponents
): boolean {
  return isEqual(dropUnrecordedVersionMismatches(tableComponents, diagramComponents), diagramComponents);
}

/** Diagram identity matches the table but recorded `syncedComponents` never stored molecule version IDs. */
function hasUnrecordedVersionBaselineOnDiagram(
  tableComponents: SyncedReactionComponents,
  diagramComponents: SyncedReactionComponents
): boolean {
  return (
    tableComponentsMatchDiagram(tableComponents, diagramComponents) &&
    !isEqual(tableComponents, diagramComponents)
  );
}

function persistSyncedComponents(
  reactionSchemeFile: NoteLinkedFile,
  structuredTable: StoichiometryStructuredTable,
  syncedComponents: SyncedReactionComponents,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): void {
  onUpdate(reactionSchemeFile, reactUpdate(structuredTable, {metadata: {$merge: {syncedComponents}}}));
}

// True while a Compound/Role/Lot cell is still evaluating (a non-failed COMPUTING value). Used to avoid
// reading reaction components before they settle on a cold load. A failed computation counts as settled.
function hasUnsettledReactionCells(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>
): boolean {
  const {compoundColumnIndex, roleColumnIndex, lotColumnIndex, versionColumnIndex} =
    getColumnIndices(columns);
  const columnIndicesToCheck = [
    compoundColumnIndex,
    roleColumnIndex,
    ...(lotColumnIndex === -1 ? [] : [lotColumnIndex]),
    ...(versionColumnIndex === -1 ? [] : [versionColumnIndex]),
  ];
  return computedTable.some((evaluatedRow) =>
    columnIndicesToCheck.some((columnIndex) => {
      const value = evaluatedRow[columnIndex]?.value;
      return value?.type === BaseTypedValueType.COMPUTING && value.computedState.status !== Status.FAILED;
    })
  );
}

interface ReactionInputIdentity {
  role: string | null;
  molecule: string;
}

// A bioentity id or SMILES string identifying a cell's molecule, deliberately excluding the display
// name (names hydrate asynchronously on a cold load, so keying on them would look like an edit).
function bioentityIdOrTextOf(cell: Cell | undefined): string {
  const typedValue = cell?.typedValue;
  if (typedValue == null) {
    return 'empty';
  }
  if (typedValue.type === BaseTypedValueType.BIOENTITY) {
    return typedValue.bioentitySummary?.id != null ? `id:${typedValue.bioentitySummary.id}` : 'empty';
  }
  if (typedValue.type === BaseTypedValueType.STRING) {
    return typedValue.rawValue ? `text:${typedValue.rawValue}` : 'empty';
  }
  return 'empty';
}

function isDiagramParticipatingRole(roleText: string | null): boolean {
  if (roleText == null) {
    return false;
  }
  const role = CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[roleText];
  return role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT;
}

// Per-row identity of the raw reaction inputs (Role plus molecule) for Reactant/Product rows only — the
// diagram ignores Catalyst/Solvent/Byproduct, so edits there must not mark the session as a diagram edit.
// Read from the persisted table at id level so it is stable across load-time resolution/hydration.
function extractReactionInputIdentity(
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): Array<ReactionInputIdentity> {
  const {compoundColumnIndex, roleColumnIndex, lotColumnIndex} = getColumnIndices(columns);
  return rows.flatMap((row) => {
    const roleCell = row.cells[roleColumnIndex];
    const roleTypedValue = roleCell?.typedValue;
    const role =
      roleTypedValue?.type === BaseTypedValueType.STRING ? roleTypedValue.rawValue : (roleCell?.text ?? null);
    if (!isDiagramParticipatingRole(role)) {
      return [];
    }
    const lotIdentity = lotColumnIndex === -1 ? 'empty' : bioentityIdOrTextOf(row.cells[lotColumnIndex]);
    const molecule =
      lotIdentity !== 'empty' ? `lot:${lotIdentity}` : bioentityIdOrTextOf(row.cells[compoundColumnIndex]);
    return [{role, molecule}];
  });
}

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex, versionColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT) {
      if (moleculeId) {
        // versionId comes straight from the evaluated Molecule Version column (computedTable),
        // so it reflects whatever Compound currently resolves to -- typed, lot-auto-linked,
        // formula, or pasted -- with no extra per-mechanism handling here.
        const versionId =
          versionColumnIndex !== -1
            ? (getMoleculeVersionIdFromEvaluatedCell(evaluatedRow[versionColumnIndex]) ?? undefined)
            : undefined;
        components[role].push({type: 'molecule', id: moleculeId, ...(versionId != null ? {versionId} : {})});
      } else {
        const smiles = rows[i]?.metadata?.canonicalSmiles;
        if (smiles) {
          components[role].push({type: 'smiles', smiles});
        }
      }
    }
  }

  return components;
}

function rowToApiComponent(
  row: StoichiometryTableRow | undefined,
  evaluatedRow: EvaluatedTable[number],
  compoundColumnIndex: number,
  ignoreDrawnMolfile: boolean
): ReactionComponentInput | null {
  if (!ignoreDrawnMolfile && row?.metadata?.drawnMolfile) {
    return {type: 'molfile', molfile: row.metadata.drawnMolfile};
  }
  const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
  if (moleculeId) {
    return {type: 'molecule', moleculeId};
  }
  const smiles = row?.metadata?.canonicalSmiles;
  if (smiles) {
    return {type: 'smiles', smiles};
  }
  return null;
}

/**
 * A drawn molfile predates version tracking and always depicts whatever the molecule looked like
 * at draw time. If the molecule has since been re-versioned (recorded and live versions both
 * known, and different), that drawing is stale and rowToApiComponent should resolve the molecule
 * fresh instead of trusting it -- matched by molecule id rather than array position, since a row
 * can contribute no entry at all to diagramComponents (e.g. drawnMolfile with no resolvable
 * molecule or smiles).
 */
function hasReVersionedSinceLastDiagramBuild(
  evaluatedRow: EvaluatedTable[number],
  compoundColumnIndex: number,
  versionColumnIndex: number,
  role: ChemistryReactionRole.REACTANT | ChemistryReactionRole.PRODUCT,
  diagramComponents: SyncedReactionComponents | undefined
): boolean {
  if (versionColumnIndex === -1 || diagramComponents == null) {
    return false;
  }
  const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
  const liveVersionId = getMoleculeVersionIdFromEvaluatedCell(evaluatedRow[versionColumnIndex]);
  if (moleculeId == null || liveVersionId == null) {
    return false;
  }
  const recordedComponent = diagramComponents[role].find(
    (component) =>
      typeof component === 'object' && component.type === 'molecule' && component.id === moleculeId
  );
  const recordedVersionId =
    typeof recordedComponent === 'object' && recordedComponent?.type === 'molecule'
      ? recordedComponent.versionId
      : undefined;
  return recordedVersionId != null && recordedVersionId !== liveVersionId;
}

function buildApiComponentsFromRows(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>,
  diagramComponents: SyncedReactionComponents | undefined
): {reactants: Array<ReactionComponentInput>; products: Array<ReactionComponentInput>} {
  const {compoundColumnIndex, roleColumnIndex, versionColumnIndex} = getColumnIndices(columns);
  const reactants: Array<ReactionComponentInput> = [];
  const products: Array<ReactionComponentInput> = [];

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);
    if (role !== ChemistryReactionRole.REACTANT && role !== ChemistryReactionRole.PRODUCT) {
      continue;
    }
    const ignoreDrawnMolfile = hasReVersionedSinceLastDiagramBuild(
      evaluatedRow,
      compoundColumnIndex,
      versionColumnIndex,
      role,
      diagramComponents
    );
    const apiComponent = rowToApiComponent(rows[i], evaluatedRow, compoundColumnIndex, ignoreDrawnMolfile);
    if (apiComponent != null) {
      (role === ChemistryReactionRole.REACTANT ? reactants : products).push(apiComponent);
    }
  }

  return {reactants, products};
}

interface PrepareAndBuildResult {
  response: BuildReactionFromComponentsResponse;
  components: SyncedReactionComponents;
}

async function prepareAndBuildUpdatedReactionDiagram(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  diagramComponents: SyncedReactionComponents | undefined
): Promise<PrepareAndBuildResult> {
  // syncedComponents tracks component identity (molecule ID or SMILES) for staleness detection.
  // buildApiComponentsFromRows separately prefers drawnMolfile in the API payload to preserve
  // user-drawn 2D orientation without resolving entity molfiles on the frontend -- unless the
  // molecule has been re-versioned since diagramComponents was last recorded, in which case the
  // drawing is stale and buildApiComponentsFromRows resolves the molecule fresh instead.
  const components = extractCurrentTableComponents(
    computedTable,
    structuredTable.columns,
    structuredTable.rows
  );
  const {reactants, products} = buildApiComponentsFromRows(
    computedTable,
    structuredTable.columns,
    structuredTable.rows,
    diagramComponents
  );
  const response = await buildReactionFromComponents(reactants, products);
  return {response, components};
}

export interface ReactionSchemeSyncState {
  /**
   * True when the diagram was already stale versus the table when the Entry opened, before any
   * edit this session. This is the case that must not auto-save (BNCH-144118); it is surfaced as a
   * banner instead so the update is attributed to a deliberate user action.
   */
  isOutOfSync: boolean;
  /** True while a diagram build (automatic or manual) is in flight. */
  isSyncing: boolean;
  /** True while a user-initiated rebuild (`syncNow`) is in flight; used to lock the table against races. */
  isManuallySyncing: boolean;
  /** Rebuilds the diagram from the current table. Intended to back the on-open "out of date" banner. */
  syncNow: () => void;
}

/**
 * Keeps the reaction scheme diagram in step with the stoichiometry table's reactants and products.
 *
 * The diagram is rebuilt by calling the chemistry service and invoking `onUpdate` with both the
 * updated NoteLinkedFile (carrying the new external file) and the updated table (with refreshed
 * syncedComponents). Rebuilding is split into two paths to avoid attributing an unintended edit to
 * whoever merely opens an Entry (BNCH-144118):
 *
 * - Automatic: once the user edits reactants/products this session, drift is synced automatically so
 *   the diagram tracks their edits with no extra clicks.
 * - Manual: drift that already existed when the Entry opened (no user edit yet) is reported via
 *   `isOutOfSync` and only rebuilt when the caller invokes `syncNow`, so simply opening a drifted
 *   Entry never triggers a save.
 *
 * A user edit is detected by baselining the raw reaction-input identity (see extractReactionInputIdentity)
 * at the first settled render, rather than the evaluated components, whose asynchronous resolution and
 * name hydration on a cold load would otherwise be misread as an edit and auto-save (BNCH-144118).
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when a new diagram is generated
 * @param enabled - Whether syncing is allowed (false in locked/viewer contexts, which cannot save)
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void,
  enabled: boolean
): ReactionSchemeSyncState {
  const diagramReactionComponents = useMemo(
    () => normalizeSyncedComponents(structuredTable.metadata.syncedComponents),
    [structuredTable.metadata.syncedComponents]
  );
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns, structuredTable.rows),
    [computedTable, structuredTable.columns, structuredTable.rows]
  );
  const reactionInputIdentity = useMemo(
    () => extractReactionInputIdentity(structuredTable.columns, structuredTable.rows),
    [structuredTable.columns, structuredTable.rows]
  );

  // The evaluated components are only trustworthy once every row is present and no reaction cell is
  // still COMPUTING; a partially-evaluated computedTable would misreport drift on a cold load.
  const isEvaluationReady =
    computedTable.length === structuredTable.rows.length &&
    !hasUnsettledReactionCells(computedTable, structuredTable.columns);

  const hasDrift =
    diagramReactionComponents == null ||
    !tableComponentsMatchDiagram(tableReactionComponents, diagramReactionComponents);

  // Baseline the reaction-input identity at the first settled render; a later change is a user edit.
  // An empty reaction settles immediately, so the first edit that populates it still counts.
  const openInputIdentityRef = useRef<Array<ReactionInputIdentity> | null>(null);
  if (openInputIdentityRef.current == null && isEvaluationReady) {
    openInputIdentityRef.current = reactionInputIdentity;
  }
  const hasUserEditedRef = useRef(false);
  if (
    !hasUserEditedRef.current &&
    openInputIdentityRef.current != null &&
    !isEqual(reactionInputIdentity, openInputIdentityRef.current)
  ) {
    hasUserEditedRef.current = true;
  }
  const hasUserEditedReactionComponents = hasUserEditedRef.current;

  // A null baseline means the table predates syncedComponents tracking; without it we cannot tell
  // whether the diagram is stale, so we do not prompt an update.
  const hasDriftVsDiagram =
    diagramReactionComponents != null &&
    (!tableComponentsMatchDiagram(tableReactionComponents, diagramReactionComponents) ||
      hasUnrecordedVersionBaselineOnDiagram(tableReactionComponents, diagramReactionComponents));

  // The banner is for pre-existing drift only, and only when a diagram exists to be out of date.
  const bannerAllowed =
    enabled && !hasUserEditedReactionComponents && reactionSchemeFile.externalFile != null;

  // Hold the last settled drift result across unsettled renders; recomputing from partial data would
  // flicker the banner off and back on during each load-time re-evaluation cycle.
  const lastSettledDriftRef = useRef(false);
  if (isEvaluationReady) {
    lastSettledDriftRef.current = hasDriftVsDiagram;
  }

  const isOutOfSync = bannerAllowed && lastSettledDriftRef.current;

  // Count in-flight builds rather than a single boolean so overlapping runs (a build canceled on
  // cleanup that resolves after its replacement started) can't clobber each other's spinner state.
  const [inFlightCount, setInFlightCount] = useState(0);
  const [manualInFlightCount, setManualInFlightCount] = useState(0);

  const runBuild = useCallback(
    (
      table: StoichiometryStructuredTable,
      evaluated: EvaluatedTable,
      options?: {manual?: boolean}
    ): {promise: Promise<void>; cancel: () => void} => {
      const manual = options?.manual ?? false;
      const {promise, cancel} = makeCancelable(
        prepareAndBuildUpdatedReactionDiagram(table, evaluated, diagramReactionComponents)
      );
      setInFlightCount((count) => count + 1);
      if (manual) {
        setManualInFlightCount((count) => count + 1);
      }
      const done = promise
        .then(({response, components}) => {
          const {externalFile, noteLinkedFile} = response;
          if (
            reactionSchemeFile.externalFile != null &&
            externalFile.id === reactionSchemeFile.externalFile.id
          ) {
            return;
          }
          onUpdate(
            serverNoteLinkedFileToClientNoteLinkedFile(noteLinkedFile),
            reactUpdate(table, {metadata: {$merge: {syncedComponents: components}}})
          );
        })
        .catch((err) => {
          // A run superseded via cancel rejects with isCanceled; that's expected. Let any real
          // failure surface instead of silently leaving the diagram out of sync.
          if (!err?.isCanceled) {
            throw err;
          }
        })
        .finally(() => {
          setInFlightCount((count) => count - 1);
          if (manual) {
            setManualInFlightCount((count) => count - 1);
          }
        });
      return {promise: done, cancel};
    },
    [diagramReactionComponents, onUpdate, reactionSchemeFile]
  );

  // Automatic sync for in-session edits. Never runs until the user has edited the reaction components
  // this session, so simply opening a drifted Entry cannot trigger a build or save.
  useEffect(() => {
    if (!enabled || !isEvaluationReady || !hasUserEditedRef.current) {
      return undefined;
    }

    if (!hasDrift) {
      return undefined;
    }

    const {cancel} = runBuild(structuredTable, computedTable);
    return cancel;
  }, [enabled, isEvaluationReady, hasDrift, structuredTable, computedTable, runBuild]);

  // Manual sync for the on-open banner. Holds the canceler so a superseding click or unmount can
  // discard a stale result rather than letting it overwrite newer data.
  const cancelManualRef = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      cancelManualRef.current?.();
      cancelManualRef.current = null;
    },
    []
  );

  const syncNow = useCallback((): void => {
    if (!isOutOfSync || !isEvaluationReady) {
      return;
    }
    if (
      diagramReactionComponents != null &&
      hasUnrecordedVersionBaselineOnDiagram(tableReactionComponents, diagramReactionComponents)
    ) {
      persistSyncedComponents(reactionSchemeFile, structuredTable, tableReactionComponents, onUpdate);
      return;
    }
    cancelManualRef.current?.();
    const {promise, cancel} = runBuild(structuredTable, computedTable, {manual: true});
    cancelManualRef.current = cancel;
    promise.finally(() => {
      if (cancelManualRef.current === cancel) {
        cancelManualRef.current = null;
      }
    });
  }, [
    isOutOfSync,
    isEvaluationReady,
    diagramReactionComponents,
    tableReactionComponents,
    structuredTable,
    computedTable,
    runBuild,
    onUpdate,
    reactionSchemeFile,
  ]);

  return {
    isOutOfSync,
    isSyncing: inFlightCount > 0,
    isManuallySyncing: manualInFlightCount > 0,
    syncNow,
  };
}
